import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Button from "@/v2-backoffice-client/components/button"
import TenantUpdateForm from "@/v2-backoffice-client/tenant/update/TenantUpdateForm"
import { useTenantDefaultsMutation } from "@/v2-backoffice-client/tenant/defaults/tenant.defaults.mutation"
import { useTenantUpdateMutation } from "@/v2-backoffice-client/tenant/update/tenant.update.mutation"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"

export type TenantUpdateDialogProps = DialogProps & {
  tenant: TenantFragment
}

// The dialog is only the shell: TenantUpdateForm (mounted inside
// Dialog.Content, so it resets by remounting each open) owns the fields and
// hands a finished payload up here, where the two mutations live.
function TenantUpdateDialog(props: TenantUpdateDialogProps) {
  const { tenant, open, onOpenChange, ...dialogProps } = props

  const [updateTenant, { loading, error }] = useTenantUpdateMutation()
  const [setDefaults, { loading: savingDefaults, error: defaultsError }] =
    useTenantDefaultsMutation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update Team</Dialog.Title>
        </Dialog.Header>

        <ErrorMessage
          className="my-4 text-center"
          error={error ?? defaultsError}
        />

        <TenantUpdateForm
          id="tenant-edit-form"
          tenant={tenant}
          onSubmit={async (data) => {
            try {
              const result = await updateTenant({
                variables: { input: { Id: tenant.Id, Name: data.Name } },
              })
              // errorPolicy is "all" app-wide, so a rejected update resolves
              // instead of throwing — closing here would swallow the error
              // banner above.
              if (result.errors?.length) return

              if (data.defaults) {
                const defaultsResult = await setDefaults({
                  variables: { input: data.defaults },
                })
                if (defaultsResult.errors?.length) return
              }

              onOpenChange?.(false)
            } catch {
              // Network errors surface via the hook error states above; keep
              // the dialog open so the operator can retry.
            }
          }}
        />

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading || savingDefaults}
            form="tenant-edit-form"
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default TenantUpdateDialog
